// §11d / §11e — the TWO AXES a Meta lead can be split on. PURE + tested.
//
// ⚠️⚠️ THEY ARE DIFFERENT QUESTIONS AND THEY WILL DISAGREE IF CONFLATED, which
// is the whole reason this module exists rather than one `split()` helper:
//
//  · LEAD ACTION — which MECHANISM recorded the lead. `metaLeads` is
//    `onsite_conversion.lead_grouped` (an on-Meta instant form), `websiteLeads`
//    is `offsite_conversion.fb_pixel_lead`.
//  · CAMPAIGN SETUP — how the CAMPAIGN was configured (objective ×
//    optimization_goal), through `campaignTypePredicate`'s own keys.
//
// They correlate and are not the same thing: a website-lead CAMPAIGN can still
// record an on-Meta lead ACTION. Every renderer names which axis it is on.
//
// ⚠️⚠️ EACH AXIS CARRIES ITS OWN RESIDUAL, AND THEY ARE DIFFERENT RESIDUALS.
// Two bars that silently fail to sum to the total above them is the trap:
//
//  · lead ACTION — `total_leads` does not always equal metaLeads + websiteLeads
//    (Meta's `leads_reconcile` flag exists for exactly this), because some
//    attribution falls outside both buckets. The remainder is REPORTED, never
//    absorbed into one of the two named buckets.
//  · campaign SETUP — the residual is every campaign that is not an
//    OUTCOME_LEADS one (traffic, engagement, awareness) plus anything whose
//    objective we could not read. That partition is exhaustive by construction
//    in the SQL, so this residual is normally zero-by-arithmetic; it is still
//    computed rather than assumed, because "it should be zero" is not a reason
//    to render a total that does not add up.
//
// ⚠️ `leadsReconcile` is TRUE for an all-zero row, so it is not a data-quality
// signal on its own. The measured residual IS the signal — that is why this
// derives it rather than reading the flag.

import type { MetaLeadBreakdown } from '../../types/advertisingHome.js';
import type { CurrencyAmount } from '../../types/reporting.js';

const r2 = (n: number): number => Math.round(n * 100) / 100;

/** One labelled slice of an axis. */
export interface LeadAxisSlice {
  key: string;
  label: string;
  leads: number;
  /** ⚠️ PER CURRENCY, never blended — a rated blend belongs at the display edge. */
  spend: CurrencyAmount[];
  /** True for the "everything else" slice, so a renderer can style it apart. */
  residual: boolean;
}

export interface LeadAxis {
  /** Which question this axis answers — rendered, not just documented. */
  title: string;
  /** The one-line explanation of the axis, shown ABOVE the bars. */
  note: string;
  slices: LeadAxisSlice[];
  /** The axis total the slices sum to. */
  totalLeads: number;
}

function amounts(map: Map<string, number>): CurrencyAmount[] {
  return [...map.entries()]
    .filter(([, amount]) => amount !== 0)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([currency, amount]) => ({ currency, amount: r2(amount) }));
}

/** Sum a field across rows, keeping money per currency. */
function fold(
  rows: readonly MetaLeadBreakdown[],
  leadsOf: (r: MetaLeadBreakdown) => number,
  spendOf: (r: MetaLeadBreakdown) => number,
): { leads: number; spend: CurrencyAmount[] } {
  const byCurrency = new Map<string, number>();
  let leads = 0;
  for (const r of rows) {
    leads += leadsOf(r);
    byCurrency.set(r.currency, (byCurrency.get(r.currency) ?? 0) + spendOf(r));
  }
  return { leads: r2(leads), spend: amounts(byCurrency) };
}

/**
 * The LEAD-ACTION axis: on-Meta instant form vs website/pixel vs the rest.
 *
 * ⚠️⚠️ THE RESIDUAL IS REAL AND IS SHOWN. `total_leads` is Meta's grouped `lead`
 * action and does not always equal the two named buckets; the difference is
 * attribution falling outside both. Folding it into either named bucket would
 * overstate that mechanism, and dropping it would leave two bars that do not
 * sum to the Leads figure the same page shows elsewhere.
 *
 * ⚠️ The residual is CLAMPED AT ZERO. The two buckets can in principle exceed
 * the grouped total, and a NEGATIVE bar is not a thing a reader can interpret;
 * `residualUnderflow` reports that it happened rather than hiding it.
 */
export function leadActionAxis(rows: readonly MetaLeadBreakdown[]): LeadAxis & {
  residualUnderflow: boolean;
} {
  const meta = fold(rows, (r) => r.metaLeads, () => 0);
  const website = fold(rows, (r) => r.websiteLeads, () => 0);
  const total = rows.reduce((n, r) => n + r.totalLeads, 0);
  const raw = r2(total - meta.leads - website.leads);
  return {
    title: 'By lead type',
    note: 'Which mechanism recorded the lead — the on-Meta instant form, or the website pixel.',
    totalLeads: r2(total),
    residualUnderflow: raw < 0,
    slices: [
      { key: 'meta', label: 'On-Meta instant form', leads: meta.leads, spend: [], residual: false },
      { key: 'website', label: 'Website (pixel)', leads: website.leads, spend: [], residual: false },
      {
        key: 'other',
        label: 'Attributed to neither',
        leads: Math.max(0, raw),
        spend: [],
        residual: true,
      },
    ],
  };
}

/**
 * The CAMPAIGN-SETUP axis: on-Meta lead campaigns vs website lead campaigns vs
 * everything else, using `campaignTypePredicate`'s own vocabulary.
 *
 * ⚠️ Spend rides this axis and not the lead-action one, because a campaign has
 * a budget and a lead action does not: Meta does not tell us what an individual
 * lead cost, only what a campaign spent.
 */
export function campaignSetupAxis(rows: readonly MetaLeadBreakdown[]): LeadAxis {
  const onMeta = fold(rows, (r) => r.onMetaCampaignLeads, (r) => r.onMetaCampaignSpend);
  const website = fold(rows, (r) => r.websiteCampaignLeads, (r) => r.websiteCampaignSpend);
  const other = fold(rows, (r) => r.otherCampaignLeads, (r) => r.otherCampaignSpend);
  return {
    title: 'By campaign setup',
    note: 'How the campaign was configured — its objective and optimisation goal.',
    totalLeads: r2(onMeta.leads + website.leads + other.leads),
    slices: [
      { key: 'on_meta_lead', label: 'On-Meta lead campaigns', ...onMeta, residual: false },
      { key: 'website_lead', label: 'Website lead campaigns', ...website, residual: false },
      {
        key: 'other',
        label: 'Other campaigns',
        ...other,
        // Traffic, engagement, awareness — and anything unreadable. Not a
        // failure to classify: these campaigns are simply not lead campaigns.
        residual: true,
      },
    ],
  };
}

/**
 * A slice's share of its axis, for the bar width.
 *
 * ⚠️ A ZERO TOTAL YIELDS NULL, NEVER 0% — an axis with nothing measured has no
 * shares, and a row of 0% bars reads as "we measured this and it was nothing".
 */
export function slicePct(slice: LeadAxisSlice, axis: LeadAxis): number | null {
  if (axis.totalLeads <= 0) return null;
  return r2((slice.leads / axis.totalLeads) * 100);
}
